'use client';

import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // Build a richer diagnostic payload for React minified errors so a
    // production console actually tells us what to fix. React's #300
    // (hydration), #310 (hooks), #185 (max-update) etc. arrive as a
    // single hashed URL — we decode the params here.
    const msg = error?.message || String(error);
    const minifiedMatch = msg.match(/react\.dev\/errors\/(\d+)\??([^\s]*)?/);

    /* eslint-disable no-console */
    console.group('[ErrorBoundary] Caught render-tree error');
    console.error('Error:', error);
    console.error('Message:', msg);
    if (minifiedMatch) {
      const [, code, params] = minifiedMatch;
      console.error('React error code:', code);
      console.error('Decoded params:', params ? decodeURIComponent(params).replace(/&/g, '\n  &') : '(none)');
      console.error('Full reference:', `https://react.dev/errors/${code}`);
      if (code === '300') {
        console.error('Cause: hydration mismatch — server-rendered HTML differs from client-rendered HTML.');
        console.error('Common sources: new Date()/Date.now() in render, localStorage in useState initializer, typeof window branches, conditional content based on user-agent.');
      }
    }
    console.error('Component stack:', info.componentStack);
    console.error('User agent:', typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a');
    console.error('URL:', typeof window !== 'undefined' ? window.location.href : 'n/a');
    console.error('Timestamp:', new Date().toISOString());
    console.groupEnd();
    /* eslint-enable no-console */
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[40vh] p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
          <h2 className="text-lg font-bold text-white mb-2">
            {this.props.fallbackTitle || 'Something went wrong'}
          </h2>
          <p className="text-sm text-gray-400 mb-4 max-w-md">
            {this.state.error?.message || 'An unexpected error occurred. Try refreshing.'}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="inline-flex items-center gap-2 px-4 py-2 bg-operator-gunmetal border border-operator-steel text-white text-sm rounded-lg hover:border-operator-gold transition"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
